import { useNavigate } from "react-router-dom";

import { Button, Input, InputNumber, Modal, Table, message } from "antd";
import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import React from "react";

import { ROUTES_URL } from "@/constant";
import { useAuthStore } from "@/stores/authStore";
import { usePledgesStore } from "@/stores/usePledgesStore";
import { MdmData, SelectedPledgeItem } from "@/types/pledgeService";
import {
  validateMinimumDownloanAmountAfterRenewalP035,
  validatePledgesMinimumDownloanAmountP034,
} from "@/utils";

import styles from "../Pledge.module.css";

interface Props {
  open: boolean;
  onClose: () => void;
  pledgesDetails: SelectedPledgeItem[];
  setSelectedRows: React.Dispatch<React.SetStateAction<SelectedPledgeItem[]>>;
  downloanAmountFromMDM: number;
  othersMdmDetails: MdmData[];
}

const DownloanPledgesDetails: React.FC<Props> = ({
  open,
  onClose,
  pledgesDetails,
  setSelectedRows,
  downloanAmountFromMDM,
  othersMdmDetails,
}) => {
  const navigate = useNavigate();
  const { currency } = useAuthStore();
  const { setPledges } = usePledgesStore();

  const getExceededPledges = () => {
    return pledgesDetails?.filter((item) => {
      const percentage = downloanAmountFromMDM / 100;
      const minRemaining = Number((percentage * item.original_offered_pledge_amount).toFixed(2));
      const maxDownloanAmount = Math.max(
        0,
        Number((item.offered_pledge_amount - minRemaining).toFixed(2)),
      );
      return item.downloanAmount > maxDownloanAmount;
    });
  };

  const onProceedClick = () => {
    // P034
    const findMinimumDownloanReduction = othersMdmDetails?.find(
      (item) => item?.reference_id === "P034",
    );
    const minimumRemainingAmount = findMinimumDownloanReduction?.value ?? 0;
    const invalidTicketAlphaCodes = validatePledgesMinimumDownloanAmountP034(
      pledgesDetails ?? [],
      minimumRemainingAmount,
    );
    if (invalidTicketAlphaCodes.length) {
      const errorMsg = `Downloan Not Allowed. Customers must choose to at least pay ${currency} ${minimumRemainingAmount} towards their loan. Pledge(s): ${invalidTicketAlphaCodes.join(", ")}.`;
      message.error(errorMsg);
      return;
    }

    // P035
    const findAfterRenewalReduction = othersMdmDetails?.find(
      (item) => item?.reference_id === "P035",
    );
    const minimumRenewalAmountForP035 = findAfterRenewalReduction?.value ?? 0;
    const invalidP035Pledges = validateMinimumDownloanAmountAfterRenewalP035(
      pledgesDetails ?? [],
      minimumRenewalAmountForP035,
    );
    if (invalidP035Pledges.length) {
      const errorMsg = `Downloan Not Allowed for pledge(s): ${invalidP035Pledges.join(", ")}. The minimum downloan amount is ${currency} ${minimumRenewalAmountForP035.toFixed(
        2,
      )}. Please enter a valid amount.`;
      message.error(errorMsg);
      return;
    }

    // Else downloan amount part
    const filterPledge = pledgesDetails?.filter((item) => item?.downloanAmount < 0);
    if (filterPledge?.length) {
      message.error(
        `Please enter a valid downloan amount for pledge ticket(s): ${filterPledge
          ?.map((item) => item?.pledge_ticket_number)
          ?.join(", ")}.`,
      );

      return;
    }
    const exceededPledges = getExceededPledges();

    if (exceededPledges?.length) {
      return;
    }
    setPledges(pledgesDetails);
    onClose();
    navigate(ROUTES_URL.CUSTOMER_CONCENT);
  };

  const updatePledgeDownloanAmount = (id: number, amount: any) => {
    setSelectedRows((prev) =>
      prev.map((item) => (item.id === id ? { ...item, downloanAmount: amount } : item)),
    );
  };

  const handleDownloanChange = (value: number | null, record: SelectedPledgeItem): void => {
    const amount = value || 0;
    const percentage = downloanAmountFromMDM / 100;
    const minRemaining = Number((percentage * record?.original_offered_pledge_amount).toFixed(2));
    const maxDownloanAmount = Math.max(
      0,
      Number((record?.offered_pledge_amount - minRemaining).toFixed(2)),
    );

    if (amount > maxDownloanAmount) {
      message.error(
        `The requested downloan amount exceeds the maximum allowed limit. Please visit the store for assistance.`,
      );
      updatePledgeDownloanAmount(record.id, maxDownloanAmount);
      return;
    }

    updatePledgeDownloanAmount(record.id, Number(amount.toFixed(2)));
  };
  const columns: ColumnsType<SelectedPledgeItem> = [
    {
      title: "Ticket Number",
      dataIndex: "ticket_alpha_code",
      key: "ticket_alpha_code",
      render: (value: string) => <span>{value}</span>,
    },
    {
      title: "Ticket Date",
      dataIndex: "pledge_date",
      key: "pledge_date",
      render: (_, record) => (
        <div> {dayjs(record?.pledge_date).format("DD MMM YYYY, hh:mm A")}</div>
      ),
    },
    {
      title: "Branch Name",
      key: "branch",
      render: (_, record) => (
        <div>
          <div>
            {record.branch?.branch_name}
            <span className="ml-1">({record.branch?.branch_code})</span>
          </div>
        </div>
      ),
    },
    {
      title: "Expiry Date",
      dataIndex: "pledge_expiry_date",
      key: "pledge_expiry_date",
      render: (_, record) => (
        <div> {dayjs(record?.pledge_expiry_date).format("DD MMM YYYY, hh:mm A")}</div>
      ),
    },
    {
      title: `Pledge Amount (${currency})`,
      dataIndex: "offered_pledge_amount",
      key: "offered_pledge_amount",
      render: (value: number | string) => <span>{value}</span>,
    },
    {
      title: `Interest (${currency})`,
      dataIndex: "total_interest_amount",
      key: "total_interest_amount",
      render: (value: number | string) => <span>{value}</span>,
    },
    {
      title: "Downloan Amount",
      width: 100,
      render: (_: unknown, record: SelectedPledgeItem): React.ReactNode => {
        return (
          <Input
            type="number"
            placeholder="Enter downloan amount"
            className="w-48"
            min={0}
            value={
              typeof record?.downloanAmount === "string"
                ? record.downloanAmount
                : Number(record?.downloanAmount || 0).toFixed(2)
            }
            onChange={(e) => {
              updatePledgeDownloanAmount(record.id, e.target.value);
            }}
            onBlur={(e) => {
              handleDownloanChange(
                e.target.value === "" ? null : Number(Number(e.target.value).toFixed(2)),
                record,
              );
            }}
          />
        );
      },
    },
  ];
  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width={"85vw"}
      title="Download Pledge Details"
    >
      <div className="mt-3">
        <Table columns={columns} dataSource={pledgesDetails} pagination={false} />
        <div className="flex justify-center my-3">
          <Button className={styles.applyButton} onClick={onProceedClick}>
            Proceed
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default DownloanPledgesDetails;
